// useCollectionChat hooks - Collection chat functionality with React Query
// Feature: Group chat per collection

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { useEffect, useRef, useState, useCallback } from 'react';
import {
  getCollectionChatMessages,
  sendCollectionChatMessage,
  pollCollectionChat,
  getCollectionChatUnreadCount,
  markCollectionChatAsRead,
  getCollectionChatRateLimitStatus,
  type CollectionMessage,
} from '../api/collection-chat';

// Query keys for cache management
export const collectionChatQueryKeys = {
  all: ['collection-chat'] as const,
  messages: (collectionId: string) => [...collectionChatQueryKeys.all, collectionId, 'messages'] as const,
  unreadCount: (collectionId: string) => [...collectionChatQueryKeys.all, collectionId, 'unread-count'] as const,
  rateLimit: (collectionId: string) => [...collectionChatQueryKeys.all, collectionId, 'rate-limit'] as const,
  poll: (collectionId: string) => [...collectionChatQueryKeys.all, collectionId, 'poll'] as const,
};

interface UseCollectionChatMessagesOptions {
  limit?: number;
  enabled?: boolean;
}

/**
 * Hook to get messages in a collection chat
 * Messages are returned in chronological order (oldest first)
 */
export function useCollectionChatMessages(
  collectionId: string | undefined,
  options: UseCollectionChatMessagesOptions = {}
) {
  const { limit = 100, enabled = true } = options;

  return useQuery({
    queryKey: [...collectionChatQueryKeys.messages(collectionId || ''), limit],
    queryFn: () => getCollectionChatMessages(collectionId!, limit),
    staleTime: 5000, // 5 seconds - messages should feel real-time
    refetchOnWindowFocus: true,
    enabled: !!collectionId && enabled,
  });
}

interface UseCollectionChatOlderMessagesResult {
  loadOlderMessages: () => Promise<CollectionMessage[]>;
  isLoading: boolean;
  hasMore: boolean;
}

/**
 * Hook for loading older messages (infinite scroll upward)
 */
export function useCollectionChatOlderMessages(
  collectionId: string | undefined,
  existingMessages: CollectionMessage[],
  limit = 50
): UseCollectionChatOlderMessagesResult {
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const loadOlderMessages = useCallback(async (): Promise<CollectionMessage[]> => {
    if (!collectionId || isLoading || !hasMore || existingMessages.length === 0) {
      return [];
    }

    setIsLoading(true);
    try {
      // Get the oldest message's timestamp
      const oldestMessage = existingMessages[0];
      const before = oldestMessage.created_at;

      const result = await getCollectionChatMessages(collectionId, limit, before);
      setHasMore(result.hasMore);
      return result.messages;
    } catch (error) {
      console.error('Failed to load older messages:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [collectionId, isLoading, hasMore, existingMessages, limit]);

  // Reset hasMore when collection changes
  useEffect(() => {
    setHasMore(true);
  }, [collectionId]);

  return {
    loadOlderMessages,
    isLoading,
    hasMore,
  };
}

/**
 * Hook to send a message to a collection chat
 */
export function useSendCollectionChatMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      collectionId,
      content,
    }: {
      collectionId: string;
      content: string;
    }) => {
      return sendCollectionChatMessage(collectionId, content);
    },
    onSuccess: (message, { collectionId }) => {
      // Invalidate messages query to show the new message
      queryClient.invalidateQueries({
        queryKey: collectionChatQueryKeys.messages(collectionId),
      });
      // Invalidate rate limit status
      queryClient.invalidateQueries({
        queryKey: collectionChatQueryKeys.rateLimit(collectionId),
      });
    },
  });
}

interface UseCollectionChatPollingOptions {
  pollingInterval?: number; // Default: 1000ms (1 second)
  enabled?: boolean;
}

/**
 * Hook for polling a collection chat for new messages
 * Only polls when enabled (i.e., when chat tab is active)
 */
export function useCollectionChatPolling(
  collectionId: string | undefined,
  options: UseCollectionChatPollingOptions = {}
) {
  const { pollingInterval = 1000, enabled = true } = options;
  const queryClient = useQueryClient();
  const lastMessageTimeRef = useRef<string | undefined>(undefined);

  const query = useQuery({
    queryKey: collectionChatQueryKeys.poll(collectionId || ''),
    queryFn: () => pollCollectionChat(collectionId!, lastMessageTimeRef.current!),
    staleTime: pollingInterval - 200,
    refetchInterval: enabled && !!lastMessageTimeRef.current ? pollingInterval : false,
    refetchIntervalInBackground: false, // Don't poll when tab is hidden
    enabled: !!collectionId && enabled && !!lastMessageTimeRef.current,
  });

  // Initialize the last message time from existing messages
  const initializeFromMessages = useCallback((messages: CollectionMessage[]) => {
    if (messages.length > 0) {
      const latestMessage = messages[messages.length - 1];
      lastMessageTimeRef.current = latestMessage.created_at;
    } else {
      // If no messages, use current time
      lastMessageTimeRef.current = new Date().toISOString();
    }
  }, []);

  // When we get new messages from polling, invalidate messages query
  useEffect(() => {
    if (query.data?.hasNewMessages && query.data.messages.length > 0) {
      const latestNew = query.data.messages[query.data.messages.length - 1];
      lastMessageTimeRef.current = latestNew.created_at;
      // Invalidate messages query to fetch new messages
      queryClient.invalidateQueries({
        queryKey: collectionChatQueryKeys.messages(collectionId!),
      });
    }
  }, [query.data, collectionId, queryClient]);

  return {
    ...query,
    hasNewMessages: query.data?.hasNewMessages ?? false,
    newMessageCount: query.data?.newMessageCount ?? 0,
    newMessages: query.data?.messages ?? [],
    initializeFromMessages,
  };
}

interface UseCollectionChatUnreadCountOptions {
  pollingInterval?: number; // Default: 30000ms (30 seconds), 0 to disable polling
  enabled?: boolean;
}

/**
 * Hook to get unread message count for a collection chat
 * Used for badge on Chat tab
 */
export function useCollectionChatUnreadCount(
  collectionId: string | undefined,
  options: UseCollectionChatUnreadCountOptions = {}
) {
  const { pollingInterval = 30000, enabled = true } = options;
  const { isAuthenticated } = useAuthStore();

  const query = useQuery({
    queryKey: collectionChatQueryKeys.unreadCount(collectionId || ''),
    queryFn: () => getCollectionChatUnreadCount(collectionId!),
    staleTime: pollingInterval > 0 ? pollingInterval - 5000 : 25000,
    refetchInterval: pollingInterval > 0 ? pollingInterval : false, // 0 means no polling
    refetchOnWindowFocus: true,
    enabled: isAuthenticated && !!collectionId && enabled,
  });

  return {
    ...query,
    unreadCount: query.data ?? 0,
  };
}

/**
 * Hook to mark a collection chat as read
 */
export function useMarkCollectionChatAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markCollectionChatAsRead,
    onSuccess: (_, collectionId) => {
      // Invalidate unread count
      queryClient.invalidateQueries({
        queryKey: collectionChatQueryKeys.unreadCount(collectionId),
      });
    },
  });
}

interface UseCollectionChatRateLimitResult {
  isLimited: boolean;
  secondsRemaining: number;
  rateLimitSeconds: number;
  isLoading: boolean;
  refetch: () => void;
}

/**
 * Hook to track rate limit status with countdown timer
 */
export function useCollectionChatRateLimit(
  collectionId: string | undefined
): UseCollectionChatRateLimitResult {
  const { isAuthenticated } = useAuthStore();
  const [countdown, setCountdown] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const query = useQuery({
    queryKey: collectionChatQueryKeys.rateLimit(collectionId || ''),
    queryFn: () => getCollectionChatRateLimitStatus(collectionId!),
    staleTime: 1000, // Check frequently
    enabled: isAuthenticated && !!collectionId,
  });

  // Update countdown when we get new rate limit data
  useEffect(() => {
    if (query.data?.isLimited && query.data.secondsRemaining > 0) {
      setCountdown(query.data.secondsRemaining);
    } else {
      setCountdown(0);
    }
  }, [query.data]);

  // Track if countdown is active to avoid restarting interval unnecessarily
  const isCountdownActive = countdown > 0;

  // Countdown timer
  useEffect(() => {
    if (isCountdownActive) {
      intervalRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            // Refetch rate limit status when countdown ends
            query.refetch();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isCountdownActive, query]);

  return {
    isLimited: countdown > 0,
    secondsRemaining: countdown,
    rateLimitSeconds: query.data?.rateLimitSeconds ?? 30,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}

// Re-export types for convenience
export type { CollectionMessage };
export { parseSystemMessage, shortenAddress } from '../api/collection-chat';
